
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { MessageSquare, Send, Paperclip, X, Smile } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import ChatBubble from './ChatBubble';
import { useChat } from '@/contexts/ChatContext';

interface TextChatProps {
  isOpen: boolean;
  soundEffects?: boolean;
  className?: string;
}

const TextChat: React.FC<TextChatProps> = ({ isOpen, soundEffects = true, className }) => {
  const [newMessage, setNewMessage] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  const messagePlayedRef = useRef<Set<string>>(new Set());
  
  const { 
    messages, 
    sendMessage, 
    isRemoteUserTyping,
    startTyping, 
    stopTyping,
    connectionStatus
  } = useChat();
  
  // Scroll to bottom when new messages are added
  useEffect(() => {
    if (chatContainerRef.current && isOpen) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    
    // Focus input when chat opens
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [messages, isOpen]);
  
  // Play sound when new message arrives, but only once per message
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      // Only play sound for incoming messages we haven't played a sound for yet
      if (
        !lastMessage.isOutgoing && 
        soundEffects && 
        !messagePlayedRef.current.has(lastMessage.id)
      ) {
        try {
          messagePlayedRef.current.add(lastMessage.id);
          const audio = new Audio('/sounds/message.mp3');
          audio.volume = 0.2;
          audio.play().catch(e => console.log('Audio play prevented:', e));
        } catch (error) {
          console.log('Audio play error:', error);
        }
      }
    }
  }, [messages, soundEffects]);
  
  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (newMessage.trim() && connectionStatus === 'connected') {
      sendMessage(newMessage.trim());
      setNewMessage('');
      
      // Play send message sound
      if (soundEffects) {
        try {
          const audio = new Audio('/sounds/send.mp3');
          audio.volume = 0.1;
          audio.play().catch(e => console.log('Audio play prevented:', e));
        } catch (error) {
          console.log('Audio play error:', error);
        }
      }
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Send typing indicator
    if (value && connectionStatus === 'connected') {
      startTyping();
    } else if (!value) {
      stopTyping();
    }
  };

  const handleClose = () => {
    if (isMobile && isOpen) {
      // Call the parent component to close the chat
      // We'll simulate this with a window event for now
      window.dispatchEvent(new CustomEvent('close-text-chat'));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Submit the form on Enter key press (but not on Shift+Enter)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return null;
  }
  
  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: isMobile ? "100%" : "30%", opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "h-screen flex flex-col bg-[#101333] border-l border-[#6C41E0]/20 z-10 hardware-accelerated",
        isMobile ? "absolute inset-0" : "relative",
        className
      )}
    >
      {/* Chat header */}
      <div className={cn(
        "p-3 bg-[#0f172a] flex items-center justify-between",
        isMobile && "p-2.5" // Smaller padding on mobile
      )}>
        <div className="flex items-center">
          <MessageSquare size={isMobile ? 16 : 20} className="text-white mr-2" />
          <h2 className={cn(
            "font-medium text-white",
            isMobile && "text-sm" // Smaller font on mobile
          )}>Chat</h2>
        </div>
        
        {isMobile && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-white/10 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
            aria-label="Close chat"
          >
            <X size={16} className="text-white" />
          </motion.button>
        )}
      </div>
      
      {/* Chat messages container */}
      <div
        ref={chatContainerRef}
        className={cn(
          "flex-1 overflow-y-auto bg-[#0c0e29] custom-scrollbar",
          isMobile ? "p-2.5" : "p-4" // Smaller padding on mobile
        )}
        tabIndex={0}
        role="log"
        aria-live="polite"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-white/50 text-center">
            <MessageSquare className={cn(
              "mb-4 opacity-20",
              isMobile ? "w-8 h-8" : "w-12 h-12" // Smaller icon on mobile
            )} />
            <p className={cn(
              "mb-2",
              isMobile && "text-sm" // Smaller text on mobile
            )}>No messages yet</p>
            <p className={cn(
              "text-sm",
              isMobile && "text-xs" // Smaller subtext on mobile
            )}>Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <ChatBubble
              key={message.id}
              message={message.text}
              isOutgoing={message.isOutgoing}
              timestamp={message.timestamp}
              isRead={message.isRead}
            />
          ))
        )}
        
        {/* Typing indicator */}
        <AnimatePresence>
          {isRemoteUserTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className={cn(
                "flex items-center space-x-1 bg-[#292D67]/50 backdrop-blur-sm rounded-full border border-[#6C41E0]/10 mt-2 w-fit",
                isMobile ? "py-1 px-2.5" : "py-1.5 px-3" // Smaller padding on mobile
              )}
            >
              <div className={cn(
                "bg-white rounded-full animate-pulse",
                isMobile ? "w-1.5 h-1.5" : "w-2 h-2" // Smaller dots on mobile
              )}></div>
              <div className={cn(
                "bg-white rounded-full animate-pulse",
                isMobile ? "w-1.5 h-1.5" : "w-2 h-2" // Smaller dots on mobile
              )} style={{ animationDelay: "0.2s" }}></div>
              <div className={cn(
                "bg-white rounded-full animate-pulse",
                isMobile ? "w-1.5 h-1.5" : "w-2 h-2" // Smaller dots on mobile
              )} style={{ animationDelay: "0.4s" }}></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Connection status message */}
      {connectionStatus !== 'connected' && (
        <div className={cn(
          "bg-[#1d2048] py-2 px-4 text-center text-white/70",
          isMobile && "py-1.5 px-3 text-xs" // Smaller on mobile
        )}>
          {connectionStatus === 'connecting' ? (
            <span>Connecting to chat partner...</span>
          ) : connectionStatus === 'failed' ? (
            <span>Connection failed. Please try again.</span>
          ) : (
            <span>Waiting for connection...</span>
          )}
        </div>
      )}
      
      {/* Chat input */}
      <form onSubmit={handleSendMessage} className={cn(
        "bg-[#0f172a] border-t border-[#6C41E0]/20",
        isMobile ? "p-2" : "p-3" // Smaller padding on mobile
      )}>
        <div className="relative flex items-center bg-[#1C214A] rounded-full px-2 py-1.5">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            type="button"
            className={cn(
              "text-white/70 hover:text-white rounded-full hover:bg-white/10 transition-colors flex items-center justify-center",
              isMobile ? "p-1.5 min-h-[36px] min-w-[36px]" : "p-2 min-h-[44px] min-w-[44px]"  // Smaller on mobile
            )}
            aria-label="Attach file"
          >
            <Paperclip size={isMobile ? 16 : 18} />
          </motion.button>
          
          <input
            ref={inputRef}
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={connectionStatus === 'connected' ? "Type a message..." : "Waiting for connection..."}
            disabled={connectionStatus !== 'connected'}
            className={cn(
              "flex-1 bg-transparent border-none outline-none px-2 py-1.5 text-white placeholder-white/50 disabled:opacity-50 font-maison",
              isMobile ? "text-sm min-h-[36px]" : "min-h-[44px]" // Smaller on mobile
            )}
            aria-label="Message input"
          />
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            type="button"
            className={cn(
              "text-white/70 hover:text-white rounded-full hover:bg-white/10 transition-colors flex items-center justify-center",
              isMobile ? "p-1.5 min-h-[36px] min-w-[36px]" : "p-2 min-h-[44px] min-w-[44px]" // Smaller on mobile
            )}
            aria-label="Emoji picker"
          >
            <Smile size={isMobile ? 16 : 18} />
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            type="submit"
            disabled={!newMessage.trim() || connectionStatus !== 'connected'}
            className={cn(
              "rounded-full transition-colors flex items-center justify-center",
              newMessage.trim() && connectionStatus === 'connected'
                ? "bg-[#6C41E0] text-white hover:bg-[#5B35C0] shadow-[0_0_10px_rgba(108,65,224,0.3)]" 
                : "bg-[#30324E] text-white/50",
              isMobile ? "p-1.5 min-h-[36px] min-w-[36px]" : "p-2 min-h-[44px] min-w-[44px]" // Smaller on mobile
            )}
            aria-label="Send message"
          >
            <Send size={isMobile ? 16 : 18} />
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default TextChat;
